// 261023 E3-P3b1 (Bước 5) — thẻ «Máy đã tự sửa» + Xem trước/sau + hộp xác nhận Hoàn tác / Dùng lại. Dùng ở Sổ duyệt (tab «Máy đã tự sửa» / «Cần bạn xem») và đầu tập ⑤.
// Mọi quyết định (nút nào hiện, chữ, diff, hệ quả hoàn tác) ở `autoFixView.ts` (hàm thuần, có jest); file này chỉ nối dây + vẽ.
// Hoàn tác / Dùng lại = endpoint restore có sẵn của Trợ lý (`restoreVersion`). 3h4c: hộp hậu quả lấy từ BE (`restore-impact`); lỗi (409 SCRIPT_CHANGED ⇒ tải lại hệ quả / khoá bận) KHÔNG tự thử lại.
import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import Button from './WbButton';
import { adaptationAssistantApi } from '../../../services/api/adaptationAssistant';
import type { EpisodeScriptRecord, FilmOutline } from '../../../types/adaptation';
import type { WorkbenchEpisode } from '../../../types/adaptationWorkbench';
import ConsequenceModal from './ConsequenceModal';
import type { ConsequenceView } from './consequenceView';
import { AutoFixCard, ConfirmDialog, DiffLine, redoDialogOf, sceneDiffLines, undoDialogOf } from './autoFixView';
import { Bang, Pill } from './WbSlot';

export const RESTORE_FAIL_FALLBACK = 'Tập vừa đổi hoặc đang có việc chạy — tải lại rồi thử lại';

interface Props {
  card: AutoFixCard;
  wbEp: WorkbenchEpisode;
  script: EpisodeScriptRecord;
  outline: FilmOutline | null;
  storyId: string;
  /** `refreshAll` của vỏ: tải lại workbench + kịch bản sau khi ghi. */
  refreshAll: () => Promise<unknown>;
  /** Có ⇒ nút ghi bị khoá kèm lý do (đang có việc chạy / đang sửa tay / chặng khoá). */
  disabledReason?: string | null;
  /** Ẩn chip «Tập N» (đầu khung tập ⑤ đã có tên tập). */
  hideEpChip?: boolean;
  /** 3k-a: mở sẵn «Xem trước/sau» (bấm «Xem» ở dòng gọn của tập). Mặc định đóng — Sổ duyệt không đổi. */
  autoOpenDiff?: boolean;
  /** 3k-a: mở sẵn hộp xác nhận Hoàn tác / Dùng lại (bấm «Hoàn tác» ở dòng gọn). Mặc định không. */
  autoConfirm?: 'undo' | 'redo' | null;
}

const TONE_CLASS: Record<'bad' | 'ok' | 'warn' | 'muted', string> = { bad: 'wb-fx-s-bad', ok: 'wb-fx-s-ok', warn: 'wb-fx-s-warn', muted: 'wb-fx-s-muted' };

const DiffColumn: React.FC<{ title: string; lines: DiffLine[]; side: 'before' | 'after' }> = ({ title, lines, side }) => (
  <div className={`wb-fx-col wb-fx-col-${side}`}>
    <div className="wb-fx-col-h">{title}</div>
    {lines.length === 0 && <div className="wb-fx-line wb-fx-line-empty">(cảnh trống)</div>}
    {lines.map((l, i) => <div key={i} className={`wb-fx-line wb-fx-line-${l.kind}`}>{l.text}</div>)}
  </div>
);

const AutoFixCardView: React.FC<Props> = ({ card, wbEp, script, outline, storyId, refreshAll, disabledReason, hideEpChip, autoOpenDiff = false, autoConfirm = null }) => {
  const [showDiff, setShowDiff] = useState(autoOpenDiff);
  const [confirm, setConfirm] = useState<'undo' | 'redo' | null>(autoConfirm);

  const nameOf = useMemo(() => (id?: string): string => {
    const c = outline?.characters.find((x) => x.id === id);
    return (c?.localizedName?.trim() || c?.name) ?? id ?? '';
  }, [outline]);
  const diffs = useMemo(() => (showDiff ? card.pairs.map((p) => ({ p, lines: sceneDiffLines(p.before, p.after, nameOf) })) : []), [showDiff, card.pairs, nameOf]);
  const dialog: ConfirmDialog | null = confirm === 'undo' ? undoDialogOf(wbEp, script) : confirm === 'redo' ? redoDialogOf(wbEp) : null;

  const versionId = confirm === 'undo' ? card.undoVersionId : confirm === 'redo' ? card.redoVersionId : null;
  const scope = { storyId, stageKey: 'script' as const, scopeKey: `ep:${card.ep}` };
  /**
   * 3h4c: «hộp hậu quả» của BE (`restore-impact`) thay hộp tự suy của FE; preview lỗi / BE cũ ⇒ hộp cũ (`dialog.becomeOld/unchanged`). `expectedModifiedAt` = mốc
   * BE trả cùng hộp (`base.scriptModifiedAt`: đúng bản người đã xem); lệch ⇒ 409 `SCRIPT_CHANGED` ⇒ hộp tải lại hệ quả. Lỗi được NÉM RA cho hộp.
   */
  const doRestore = async (view: ConsequenceView | null) => {
    if (!versionId) return;
    const base = view?.base.scriptModifiedAt;
    const expected = typeof base === 'string' && base ? base : script.modifiedAt;
    if (!expected) { setConfirm(null); message.error('Thiếu dấu thời gian của tập — tải lại trang rồi thử lại'); return; }
    await adaptationAssistantApi.restoreVersion(versionId, { ...scope, expectedModifiedAt: expected });
    await refreshAll();
    message.success(confirm === 'undo' ? `Đã hoàn tác tập ${card.ep} về v${card.preVersionNo}` : `Đã dùng lại bản máy sửa của tập ${card.ep}`, 6);
  };

  const writeOff = !!disabledReason;
  return (
    <div className={`wb-fx wb-fx-${card.state}`} data-testid="autofix-card" data-state={card.state} data-ep={card.ep}>
      <div className="wb-fx-h">
        {!hideEpChip && <Pill tone="acc">Tập {card.ep}</Pill>}
        <Pill tone={card.state === 'undone' ? 'warn' : 'ok'}>Máy đã tự sửa</Pill>
        <b className="wb-fx-sum" data-testid="autofix-summary">{card.summary}</b>
      </div>
      {card.steps.length > 0 && (
        <div className="wb-fx-steps" aria-label="Chuỗi bước">
          {card.steps.map((s, i) => (
            <React.Fragment key={`${s.key}-${i}`}>
              {i > 0 && <span className="wb-fx-arrow" aria-hidden="true">→</span>}
              <span className={`wb-fx-step ${TONE_CLASS[s.tone]}`}>{s.text}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {card.scenes.length > 0 && <div className="wb-fx-sub">Cảnh đã sửa: {card.scenes.map((s) => s.label).join(', ')}</div>}
      {card.orangeNote && <div className="wb-fx-note wb-fx-note-warn" data-testid="autofix-orange"><Bang tone="warn" /> <span>{card.orangeNote}</span></div>}
      {card.greyNote && <div className="wb-fx-note wb-fx-note-grey" data-testid="autofix-grey">{card.greyNote}</div>}
      {(card.canDiff || card.canUndo || card.canRedo) && (
        <div className="wb-fx-actions">
          {card.canDiff && <Button size="small" onClick={() => setShowDiff((v) => !v)} aria-expanded={showDiff}>{showDiff ? 'Ẩn trước/sau' : 'Xem trước/sau'}</Button>}
          {card.canUndo && <Button size="small" danger disabled={writeOff} title={disabledReason ?? undefined} onClick={() => setConfirm('undo')}>{card.undoLabel}</Button>}
          {card.canRedo && <Button size="small" disabled={writeOff} title={disabledReason ?? undefined} onClick={() => setConfirm('redo')}>{card.redoLabel}</Button>}
        </div>
      )}
      {showDiff && diffs.map(({ p, lines }) => (
        <div className="wb-fx-diff" key={p.scenePlanId || p.beforeTitle} data-testid="autofix-diff">
          <DiffColumn title={p.beforeTitle} side="before" lines={lines.filter((l) => l.kind !== 'add')} />
          <DiffColumn title={p.afterTitle} side="after" lines={lines.filter((l) => l.kind !== 'del')} />
        </div>
      ))}

      <ConsequenceModal
        open={!!dialog && !!versionId} onClose={() => setConfirm(null)} danger={confirm === 'undo'}
        title={dialog?.title ?? ''} lead={dialog?.lead ?? ''} okText={dialog?.okText ?? ''}
        load={() => adaptationAssistantApi.restoreImpact(versionId!, scope)}
        errorFallback={RESTORE_FAIL_FALLBACK}
        fallback={dialog && (
          <div className="wb-fx-modal">
            {dialog.becomeOld.length > 0 && (
              <div className="wb-fx-box wb-fx-box-bad">
                <b>Sẽ thành Cũ / cần làm lại</b>
                <ul>{dialog.becomeOld.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </div>
            )}
            {dialog.unchanged.length > 0 && (
              <div className="wb-fx-box wb-fx-box-grey">
                <b>Không đổi</b>
                <ul>{dialog.unchanged.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </div>
            )}
          </div>
        )}
        onConfirm={doRestore}
      />
    </div>
  );
};

export default AutoFixCardView;
